import raw from "./page-registry.json";
import { PLAN_LIVE_TABS, PLAN_TABS, type PlanTab, placeholderNote } from "./shell/plan-tabs";

/**
 * Plan 页面注册表（单一出处，后台页面对齐 §八 8.3）。数据在 `page-registry.json`，这里只给类型和派生：
 * - 左侧 Plan 菜单（`routes/__root.tsx` 读 `PLAN_NAV_GROUPS` 生成，不再手写）；
 * - 链路总览的节点跳转（`resolveNodeLink`，原 `overview/node-links.ts` 已并进来）；
 * - 页面地图（按一级分组、依赖 ／ 被依赖；选中一页时详情区显示它的主要能力 caps）。
 * 页面名与页头说明仍在 `shell/plan-tabs.ts`（页名从那里取，不在注册表里重复写）。
 * 核查：`node apps/admin/scripts/plan-registry-audit.mjs`（R1～R10；e2e `plan-registry.spec.ts` 与 check_plan_pages S15 调同一份）。
 */

export type PageSource = "平台内置" | "系统计算" | "客户自定义";
export type OriginKind = "代码" | "业务包" | "接口" | "仓库文档";
export type PlanGroupKey =
	| "overview"
	| "modes"
	| "results"
	| "eval-config"
	| "chain"
	| "business"
	| "knowledge"
	| "base";
export type ChainStepKey =
	| "signal"
	| "board"
	| "recall"
	| "decide"
	| "execute"
	| "settle"
	| "reply"
	| "judge"
	| "sim";

export type PlanGroup = {
	key: PlanGroupKey;
	/** 侧栏分组 id（必须 plan- 开头，侧栏按前缀归到 Plan 段） */
	navId: string;
	testId: string;
	label: string;
	/** 这一组回答什么 */
	answer: string;
	/** 来源（对标签体系 v2）：一组的总体口径 */
	origin: string;
	/** 页面地图的层：0 总览 1 定义 2 内容 3 链路 4 评测 5 分析；依赖只能从小层读到大层 */
	layer: number;
	/** 这一组在链路总览里的统一配色 */
	tone: GroupTone;
};

export type GroupTone = "neutral" | "primary" | "success" | "info" | "warning";

/** 一级的配色（页面地图与运行时流程同一套）：竖条、徽标底色、圆点。类名写全，Tailwind 才能扫到 */
export const GROUP_TONE: Record<GroupTone, { bar: string; chip: string; dot: string }> = {
	neutral: {
		bar: "border-l-text-muted",
		chip: "bg-surface-subtle text-text-secondary",
		dot: "bg-text-muted",
	},
	primary: {
		bar: "border-l-brand-primary",
		chip: "bg-brand-primary/10 text-brand-primary",
		dot: "bg-brand-primary",
	},
	success: {
		bar: "border-l-brand-success",
		chip: "bg-brand-success/10 text-brand-success",
		dot: "bg-brand-success",
	},
	info: {
		bar: "border-l-brand-info",
		chip: "bg-brand-info/10 text-brand-info",
		dot: "bg-brand-info",
	},
	warning: {
		bar: "border-l-brand-warning",
		chip: "bg-brand-warning/10 text-brand-warning",
		dot: "bg-brand-warning",
	},
};

export type PageDep = { page: PlanTab; what: string };

/** 页面的一条主要能力（行业通用话术）；step ＝ 落在运行时第几步，必须是本页登记的 steps 之一（核查 R4） */
export type PageCap = { text: string; step?: ChainStepKey };

export type LayerSource = "rules" | "guidance" | "knowledge" | "tools";

export type PlanPageEntry = {
	id: PlanTab;
	/** 页面名（取自 plan-tabs.ts 的 title） */
	name: string;
	group: PlanGroupKey;
	answer: string;
	source: PageSource;
	placeholder?: boolean;
	/** 页面地图里不画（只给链路总览自己：地图就在它里面） */
	hideInMap?: boolean;
	origin: { kind: OriginKind; path: string }[];
	steps: ChainStepKey[];
	/** 主要能力（页面地图详情区显示）；没有就不列 */
	caps?: PageCap[];
	/** 对这些环节是「管」（约束做法或输入）；不写就是「读」（运行时取用） */
	governs?: boolean;
	/** 页内容带层字段，数据取自哪个接口（rules ／ guidance ／ knowledge ／ tools）；页面地图详情区按它出层分布，没有就不显示 */
	layerFrom?: LayerSource;
	/** 规划页的状态说明（没有能力和环节，页面地图详情区如实写） */
	statusNote?: string;
	deps: PageDep[];
	jumps: PlanTab[];
};

type RawPage = Omit<PlanPageEntry, "name">;

/** 页面地图的层；flow ＝ 这一层到下一层的关系（层间箭头旁的小字） */
export const PLAN_LAYERS = raw.layers as { layer: number; label: string; flow?: string }[];
export const PLAN_GROUPS = raw.groups as PlanGroup[];
/** 七个环节的说明正本（做什么、产出、不管、关键机制名；每项 ≤30 字，审计 R12）；ruleStage ＝ 运行规则页「环节」列里对应的取值 */
export type ChainStepDef = {
	key: ChainStepKey;
	label: string;
	ruleStage?: string;
	what?: string;
	out?: string;
	not?: string;
	mechs?: string[];
};
export const CHAIN_STEPS = raw.chainSteps as ChainStepDef[];

/** 一轮开头 ／ 结尾之外的补充一步：钉住配置快照（基线 04 §一） */
export const RUNTIME_EXTRAS = raw.runtimeExtras as {
	pin: { name: string; what: string };
	/** 不在七步内的规则环节（运行规则页「环节」列取值）：放在事实表横带上 */
	factStages: string[];
};

/** 承载对象（基线 03 矩阵：对象 × 七步；基线 02 三之一：对象 × 三层）。页面注册表是唯一出处，组件不写映射 */
export type PlanObject = {
	key: string;
	label: string;
	/** 对象的维护页；方案阶段的对象没有 */
	page: PlanTab | null;
	/** 方案阶段（Skill）：虚线标注 */
	planned?: boolean;
	/** 各环节里它管什么（没有的环节不写） */
	steps: Partial<Record<ChainStepKey, string>>;
	/** 三层各放什么；事实、配置不分层，没有这一项 */
	layers?: { L1: string; L2: string; L3: string };
};
export const PLAN_OBJECTS = raw.objects as unknown as PlanObject[];
export const OBJECT_LAYERS = raw.objectLayers as {
	key: "L1" | "L2" | "L3";
	label: string;
	test: string;
}[];

/** 某一环节用到哪些对象（对象 × 七步矩阵的一列） */
export function objectsOfStep(step: ChainStepKey): PlanObject[] {
	return PLAN_OBJECTS.filter((o) => o.steps[step]);
}

export const PLAN_PAGES: PlanPageEntry[] = (raw.pages as unknown as RawPage[]).map((p) => ({
	...p,
	name: PLAN_TABS[p.id].title,
}));

const BY_ID = new Map(PLAN_PAGES.map((p) => [p.id, p]));
const GROUP_BY_KEY = new Map(PLAN_GROUPS.map((g) => [g.key, g]));

export function planPage(id: PlanTab): PlanPageEntry {
	return BY_ID.get(id) as PlanPageEntry;
}

export function planGroup(key: PlanGroupKey): PlanGroup {
	return GROUP_BY_KEY.get(key) as PlanGroup;
}

/** 一级所在的层名（定义 ／ 内容 ／ 链路 ／ 评测 ／ 分析），页面地图分层与组头徽标用同一个 */
export function layerLabel(layer: number): string {
	return PLAN_LAYERS.find((l) => l.layer === layer)?.label ?? "";
}

export function pagesOfGroup(key: PlanGroupKey): PlanPageEntry[] {
	return PLAN_PAGES.filter((p) => p.group === key);
}

/** 被依赖（反查）：哪些页面读了这一页的定义，各读什么 */
export function dependentsOf(id: PlanTab): { page: PlanTab; what: string }[] {
	const out: { page: PlanTab; what: string }[] = [];
	for (const p of PLAN_PAGES) {
		for (const d of p.deps) if (d.page === id) out.push({ page: p.id, what: d.what });
	}
	return out;
}

/** 来源徽标文字：客户自定义本期由我们代维护 */
export const SOURCE_LABEL: Record<PageSource, string> = {
	平台内置: "平台内置",
	系统计算: "系统计算",
	客户自定义: "客户自定义·代维护",
};

/** 侧栏 Plan 段：按注册表的一级顺序与页面顺序 */
export const PLAN_NAV_GROUPS: (PlanGroup & { pages: PlanPageEntry[] })[] = PLAN_GROUPS.map((g) => ({
	...g,
	pages: pagesOfGroup(g.key),
}));

// ── 页面能不能跳 ─────────────────────────────────────────

export type ResolvedLink = { tab: PlanTab } | { tab: null; why: string };

function isLive(tab: PlanTab): boolean {
	return PLAN_LIVE_TABS.has(tab) && !PLAN_TABS[tab].placeholder;
}

/** 页面 → 能跳的 hash；占位 ／ 没上线时给一句原因（悬停提示用） */
export function resolvePage(tab: PlanTab): ResolvedLink {
	if (isLive(tab)) return { tab };
	const phase = PLAN_TABS[tab].placeholder;
	return {
		tab: null,
		why: phase ? `「${PLAN_TABS[tab].title}」${placeholderNote(phase)}` : "页面未上线",
	};
}

// ── 链路总览节点 → 页面 ──────────────────────────────────

type RawNode = { page: PlanTab | null; label: string; why?: string };
const NODES = raw.overviewNodes as unknown as Record<string, RawNode>;

/** 总览图里的节点键（只能用注册表 overviewNodes 里登记过的） */
export type NodeLinkKey = keyof typeof raw.overviewNodes;

/** 节点归哪个页面（运行时流程每一步标「属于应答链路下哪一页」用） */
export function nodePage(key: NodeLinkKey): PlanTab | null {
	return NODES[key]?.page ?? null;
}

/** 节点 → 能跳的 hash；节点没有页面时给登记的原因 */
export function resolveNodeLink(key: NodeLinkKey): ResolvedLink {
	const n = NODES[key];
	if (!n?.page) return { tab: null, why: n?.why ?? "暂无对应页面" };
	return resolvePage(n.page);
}
